import type { Components, Theme } from '@mui/material/styles';

import { filledInputClasses } from '@mui/material/FilledInput';

// Component-level overrides. Add brand-specific defaults + sx tweaks here as the design system stabilizes.

export function getComponents(theme: Theme): Components<Theme> {
  return {
    MuiButton: {
      // Adapted from the v1 reference theme (`Sahbi/.../theme/core/components/button.tsx`).
      // Color defaults to `inherit` so buttons read as neutral unless a palette color is
      // requested explicitly (`color="primary"`). Sizing standardizes vertical rhythm.
      defaultProps: {
        color: 'inherit',
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          letterSpacing: 0,
        },
        // currentColor border at low alpha; hover adds a 0.75px ring + faint tint of the
        // current text color. Works for any color="inherit"|palette button uniformly.
        outlined: {
          borderColor: 'color-mix(in srgb, currentColor 32%, transparent)',
          '&:hover': {
            borderColor: 'currentColor',
            boxShadow: '0 0 0 0.75px currentColor',
            backgroundColor: 'color-mix(in srgb, currentColor 4%, transparent)',
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'color-mix(in srgb, currentColor 4%, transparent)',
          },
        },
        // Size system: stable min-heights + paddings so adjacent buttons line up.
        sizeSmall: { minHeight: 30, padding: '4px 8px' },
        sizeMedium: { minHeight: 36, padding: '6px 12px' },
        sizeLarge: { minHeight: 48, padding: '8px 16px' },
        // No styleOverrides.root.borderRadius — MUI's default uses theme.shape.borderRadius
        // (12 in our theme), so every <Button> auto-picks up the global radius unless its
        // sx explicitly overrides for round/pill cases.
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          minHeight: '100vh',
        },
      },
    },
    // Filled-input variant used by the auth forms (login, register). Mirrors v1's pattern:
    // no underline, theme-global rounded corners, subtle sage-tinted background that
    // darkens slightly on hover/focus. Outlined inputs (the default elsewhere) are
    // unchanged.
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          backgroundColor: `${theme.palette.primary.main}0F`,
          borderRadius: Number(theme.shape.borderRadius),
          [`&:hover, &.${filledInputClasses.focused}`]: {
            backgroundColor: `${theme.palette.primary.main}1A`,
          },
          [`&.${filledInputClasses.error}`]: {
            backgroundColor: `${theme.palette.error.main}14`,
            [`&:hover, &.${filledInputClasses.focused}`]: {
              backgroundColor: `${theme.palette.error.main}1F`,
            },
          },
        },
      },
    },
  };
}
